// Modal-Picker fuer Method-Templates. Slash-Trigger im Canvas oeffnet das Modal,
// Klick auf ein Tile ruft onPick mit MethodId. Nutzt natives <dialog> mit showModal()
// — bekommt damit Escape-Handling, Backdrop-Klick und A11y geschenkt.

import {
  METHOD_CATALOG,
  type MethodId,
  USE_CASES,
  type UseCaseId,
  methodsForUseCase,
} from "@ms-whiteboard/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./MethodPickerModal.css";

interface MethodPickerModalProps {
  open: boolean;
  onPick: (methodId: MethodId) => void;
  onClose: () => void;
}

export function MethodPickerModal({
  open,
  onPick,
  onClose,
}: MethodPickerModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const [activeUc, setActiveUc] = useState<UseCaseId>("uc-01-ist");
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Backdrop-Klick: das <dialog>-Element selbst empfaengt den Klick, das Inhalts-<form>
  // konsumiert ihn. Ein direkter Klick auf das Dialog ohne Inhalt ist somit Backdrop.
  const onDialogClick = (e: React.MouseEvent<HTMLDialogElement>): void => {
    if (e.target === dialogRef.current) onClose();
  };

  const ucMethods = methodsForUseCase(activeUc);
  const otherMethods = METHOD_CATALOG.filter((m) => !m.useCases.includes(activeUc));

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: natives <dialog> handlet Escape selbst, onClick filtert Backdrop-Klick
    <dialog
      ref={dialogRef}
      className="method-picker-dialog"
      onClose={onClose}
      onClick={onDialogClick}
    >
      <form method="dialog" className="method-picker-form">
        <header className="method-picker-header">
          <h2>{t("methodPicker.title")}</h2>
          <button
            type="button"
            className="method-picker-close"
            onClick={onClose}
            aria-label={t("methodPicker.close")}
          >
            ×
          </button>
        </header>
        <div className="method-picker-tabs" role="tablist">
          {USE_CASES.map((uc) => (
            <button
              key={uc.id}
              type="button"
              role="tab"
              aria-selected={activeUc === uc.id}
              className={`method-picker-tab ${activeUc === uc.id ? "is-active" : ""}`}
              onClick={() => setActiveUc(uc.id)}
            >
              <span className="method-picker-tab-code">{uc.shortCode}</span>
              <span>{t(uc.nameKey)}</span>
            </button>
          ))}
        </div>
        <div className="method-picker-body">
          <div className="method-picker-grid">
            {ucMethods.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`method-picker-tile is-primary method-family-${m.family}`}
                onClick={() => onPick(m.id)}
              >
                <span className="method-picker-tile-name">{t(m.nameKey)}</span>
                <span className="method-picker-tile-desc">{t(m.descriptionKey)}</span>
              </button>
            ))}
          </div>
          {otherMethods.length > 0 && (
            <>
              <h3 className="method-picker-sub">{t("methodPicker.otherMethods")}</h3>
              <div className="method-picker-grid">
                {otherMethods.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`method-picker-tile method-family-${m.family}`}
                    onClick={() => onPick(m.id)}
                  >
                    <span className="method-picker-tile-name">{t(m.nameKey)}</span>
                    <span className="method-picker-tile-desc">{t(m.descriptionKey)}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </form>
    </dialog>
  );
}
